/**
 * Level-Based Action Unlocks
 *
 * Defines which combat actions become available at each level.
 * This creates a progression system where players unlock abilities as they level up.
 */

import type { CombatAction } from '../types/pvpTypes.js';

/**
 * Level thresholds for unlocking combat actions.
 */
export const ACTION_UNLOCK_LEVELS = {
  /** Attack is always available from level 1 */
  ATTACK: 1,
  /** Dodge unlocks at level 1 */
  DODGE: 1,
  /** Recover unlocks at level 1 (with 1 use per battle) */
  RECOVER: 1,
  /** Magic unlocks at level 1 */
  MAGIC: 1,
} as const;

/**
 * Level thresholds for recover use increases.
 * Recover is available from level 1 with 1 use, then gains more uses at higher levels.
 */
export const RECOVER_USE_THRESHOLDS = {
  /** 1 recover use starting at level 1 */
  ONE_USE: 1,
  /** 2 recover uses starting at level 6 */
  TWO_USES: 6,
  /** 3 recover uses starting at level 15 */
  THREE_USES: 15,
} as const;

/**
 * Calculate max recover uses based on level.
 *
 * Progression:
 * - Level 1-5: 1 use
 * - Level 6-14: 2 uses
 * - Level 15+: 3 uses
 *
 * @param level - Current level (1-60+)
 * @returns Maximum recover uses per battle
 */
export function getMaxRecoverUsesForLevel(level: number): number {
  if (level < RECOVER_USE_THRESHOLDS.TWO_USES) return 1;
  if (level < RECOVER_USE_THRESHOLDS.THREE_USES) return 2;
  return 3;
}

/**
 * Check if an action is unlocked at a given level.
 *
 * @param action - Combat action to check
 * @param level - Current level (1-60+)
 * @param hasMagic - Whether species has magic ability (species-dependent)
 * @returns true if action is unlocked at this level
 */
export function isActionUnlocked(
  action: CombatAction,
  level: number,
  hasMagic = true
): boolean {
  switch (action) {
    case 'attack':
      return level >= ACTION_UNLOCK_LEVELS.ATTACK;
    case 'dodge':
      return level >= ACTION_UNLOCK_LEVELS.DODGE;
    case 'recover':
      return level >= ACTION_UNLOCK_LEVELS.RECOVER;
    case 'magic':
      return hasMagic && level >= ACTION_UNLOCK_LEVELS.MAGIC;
    default:
      return false;
  }
}

/**
 * Get all unlocked actions for a given level.
 *
 * @param level - Current level (1-60+)
 * @param hasMagic - Whether species has magic ability
 * @returns Array of unlocked actions
 *
 * @example
 * getUnlockedActions(1, true)  // ['attack', 'magic', 'dodge', 'recover']
 * getUnlockedActions(1, false) // ['attack', 'dodge', 'recover']
 */
export function getUnlockedActions(level: number, hasMagic = true): CombatAction[] {
  const actions: CombatAction[] = [];

  if (isActionUnlocked('attack', level, hasMagic)) actions.push('attack');
  if (isActionUnlocked('magic', level, hasMagic)) actions.push('magic');
  if (isActionUnlocked('dodge', level, hasMagic)) actions.push('dodge');
  if (isActionUnlocked('recover', level, hasMagic)) actions.push('recover');

  return actions;
}

/**
 * Get a fallback action when the desired action is locked.
 * Always returns 'attack' as it's the only action guaranteed to be unlocked at level 1.
 *
 * @returns 'attack' (always available)
 */
export function getLockedActionFallback(): CombatAction {
  return 'attack';
}
